import {
  inboxQueryKey,
  markInboxRead,
  notificationsApiConfigured,
  recordInboxClick,
} from '@/common/apis/services/notifications/inbox';
import classNames from '@/common/utils/classNames';

import { useUserInfoStore } from '@/modules/login/store/userInfo';

import { openNotificationUrl } from '@/common/utils/openNotificationUrl';
import { useQueryClient } from '@tanstack/react-query';
import { useRouter } from 'next/router';
import { useEffect, useRef, useState } from 'react';

import { DsCard, DsSectionHeader, ds } from '../designSystem';

import { useSelectedDateStore } from '../store/selectedDate';

import { useSheetDrawerProps } from '../hooks/doctorHomeSheetLayout';
import { useSheetRoute } from '../hooks/useSheetRoute';

import { DoctorHomeFeedAlert } from '../types/feed';

import { sendDoctorHomeEvent } from '../utils/analytics';

import {

  formatSelectedDateLabel,

  getNotificationDateKey,

  getNotificationFullText,

  getNotificationHeadline,

  getNotificationPreview,

} from '../utils/normalizeNotification';

import { DsDrawer } from './DsDrawer';

import { dsFocusRing } from '../utils/a11y';

import { FeedStaggerItem } from './feedContentSwap';
import { BellIcon, RowChevronIcon } from './icons';



interface NotificationsSectionProps {

  items: DoctorHomeFeedAlert[];

  className?: string;

  widgetShell?: boolean;

}



export const NotificationsSection = ({ items, className, widgetShell = false }: NotificationsSectionProps) => {

  const userId = useUserInfoStore(state => state.info?.id);
  const queryClient = useQueryClient();
  const router = useRouter();

  const selectedDate = useSelectedDateStore(state => state.selectedDate);

  const notificationSheet = useSheetRoute('notification');
  const notificationDrawerProps = useSheetDrawerProps(notificationSheet);

  const [selectedNotification, setSelectedNotification] = useState<DoctorHomeFeedAlert | null>(null);

  const selectedDateLabel = formatSelectedDateLabel(selectedDate);



  // Mark read when the sheet closes: marking on open drops the item from the
  // unread-only list and would unmount this section (and its sheet) mid-read.
  const sheetWasOpen = useRef(false);
  useEffect(() => {
    const wasOpen = sheetWasOpen.current;
    sheetWasOpen.current = notificationSheet.open;
    if (notificationSheet.open || !wasOpen || !selectedNotification) return;
    const id = selectedNotification.id;
    setSelectedNotification(null);
    if (notificationsApiConfigured() && id != null) {
      void markInboxRead(String(id)).then(() => {
        void queryClient.invalidateQueries(inboxQueryKey);
      });
    }
  }, [notificationSheet.open, selectedNotification, queryClient]);



  if (!items.length && !selectedNotification) return null;



  const openNotification = async (notification: DoctorHomeFeedAlert, index: number) => {
    sendDoctorHomeEvent(userId, 'notification_click', {
      title: getNotificationHeadline(notification),
      index,
      count: items.length,
      selected_date: selectedDate,
      has_url: !!notification.url,
    });

    const trackable = notificationsApiConfigured() && notification.id != null;

    // Linked notifications go straight to their target, like the header bell.
    if (notification.url) {
      if (trackable) {
        // Wait for both before navigating so a full page load does not cancel them.
        await Promise.allSettled([recordInboxClick(String(notification.id)), markInboxRead(String(notification.id))]);
        void queryClient.invalidateQueries(inboxQueryKey);
      }
      openNotificationUrl(notification.url, router);
      return;
    }

    if (trackable) void recordInboxClick(String(notification.id));

    setSelectedNotification(notification);
    notificationSheet.openSheet({
      notification_id: String(notification.id ?? index),
    });
  };



  const selectedHeadline = selectedNotification ? getNotificationHeadline(selectedNotification) : '';

  const selectedFullText = selectedNotification ? getNotificationFullText(selectedNotification) : '';



  return (

    <section dir="rtl" className={className}>

      <DsSectionHeader

        title="اعلان‌ها"

        subtitle={`${items.length.toLocaleString('fa-IR')} پیام · ${selectedDateLabel}`}

      />



      <DsCard padding="none" variant={widgetShell ? 'widget' : 'default'} className="overflow-hidden">

        <ul>

          {items.map((notification, index) => {

            const headline = getNotificationHeadline(notification);

            const preview = getNotificationPreview(notification);



            return (

              <li key={notification.id ?? `${headline}-${index}`}>
                <FeedStaggerItem index={index}>
                <button

                  type="button"

                  onClick={() => {
                    void openNotification(notification, index);
                  }}

                  aria-label={notification.url ? `${headline}، باز کردن` : `${headline}، مشاهده کامل`}

                  className={classNames(

                    'flex w-full items-start gap-3 text-start',

                    ds.motion.listRow,

                    ds.layout.rowPaddingWide,

                    dsFocusRing,

                    index < items.length - 1 && 'border-b border-slate-100',

                  )}

                >

                  <span className={classNames('mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-amber-600', ds.surface.warningSoft)}>

                    <BellIcon size="sm" />

                  </span>



                  <div className="min-w-0 flex-1">

                    <p className={classNames(ds.type.cardTitle, 'leading-6')}>{headline}</p>

                    {preview ? (

                      <p className={classNames(ds.type.cardBody, 'mt-1 line-clamp-3 leading-6')}>{preview}</p>

                    ) : null}

                  </div>
                  {notification.url ? <RowChevronIcon size="sm" className="mt-2 shrink-0" /> : null}

                </button>
                </FeedStaggerItem>

              </li>

            );

          })}

        </ul>

      </DsCard>



      <DsDrawer

        {...notificationDrawerProps}

        title={selectedHeadline}

        description={selectedNotification ? selectedDateLabel : undefined}

      >

        <div className="px-4 pb-8">

          <p className={classNames(ds.type.cardBody, 'whitespace-pre-wrap leading-7')}>{selectedFullText}</p>

        </div>

      </DsDrawer>

    </section>

  );

};


